import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Input } from "../components/ui/input";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/tabs";
import {
  ALL_PRODUCTS_GROUPED,
  MONTHS3_PRODUCTS,
  MONTHS6_PRODUCTS,
} from "../data/products";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useLang } from "../i18n/LanguageContext";
import { Loader2, MessageCircle, Mail } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_janmobile-checkout/artifacts/44154y5j_JanMobileLogo.png";
const WHATSAPP = "+32488838808";
const SUPPORT_EMAIL = "sales@janmobile.be";

const formatPrice = (p) => `\u20AC ${p.toFixed(2).replace(".", ",")}`;
const normalize = (s) => (s || "").replace(/\s+/g, "");

const ProductRow = ({ product, onBuy, loadingId, t }) => {
  const busy = loadingId === product.id;
  return (
    <tr
      data-testid={`product-row-${product.id}`}
      className="border-b border-orange-100 last:border-b-0 hover:bg-orange-50/40 transition"
    >
      <td className="bg-white px-2.5 sm:px-3 py-1.5 align-middle w-full">
        <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-wrap">
          <span
            data-testid={`product-label-${product.id}`}
            className="text-[13px] sm:text-sm font-extrabold text-orange-950 whitespace-nowrap"
          >
            {product.prefixKey ? `${t(product.prefixKey)} ` : ""}
            {product.label}
          </span>
          {product.bonus > 0 && (
            <span
              data-testid={`bonus-badge-${product.id}`}
              className="inline-flex items-center bg-green-100 text-green-800 text-[10px] sm:text-[11px] font-extrabold uppercase tracking-wide px-1.5 py-0.5 rounded-md whitespace-nowrap border border-green-200"
            >
              + {product.bonus}GB {t("freeWord")}
            </span>
          )}
          {product.popular && (
            <span
              data-testid={`popular-badge-${product.id}`}
              className="brand-gradient text-white text-[9px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded-full shadow-sm whitespace-nowrap"
            >
              {product.id.startsWith("data-") ? t("bestDeal") : t("popular")}
            </span>
          )}
        </div>
        {product.sub && (
          <div className="sm:hidden text-[10px] font-semibold text-orange-700/70 mt-0.5 whitespace-nowrap">
            {product.sub}
          </div>
        )}
      </td>
      <td className="hidden sm:table-cell bg-orange-50/80 px-3 py-1.5 align-middle text-center border-l border-orange-100 whitespace-nowrap">
        <span
          data-testid={`product-sub-${product.id}`}
          className="text-[11px] font-semibold text-orange-700/80"
        >
          {product.sub || "\u2014"}
        </span>
      </td>
      <td className="bg-orange-100/60 px-2 sm:px-3 py-1.5 align-middle text-right border-l border-orange-100 whitespace-nowrap">
        <div
          data-testid={`product-price-${product.id}`}
          className="text-[12px] sm:text-sm font-extrabold text-orange-800 tabular-nums leading-tight"
        >
          {formatPrice(product.price)}
        </div>
        {product.perMonth && (
          <div className="text-[9px] sm:text-[10px] font-semibold text-orange-600/80 tabular-nums leading-tight mt-0.5">
            {formatPrice(product.perMonth)}
            {t("perMonthShort")}
          </div>
        )}
      </td>
      <td className="bg-white pl-1 pr-1.5 sm:pl-1.5 sm:pr-2 py-1 align-middle whitespace-nowrap">
        <button
          data-testid={`buy-button-${product.id}`}
          onClick={() => onBuy(product)}
          disabled={busy}
          className="koop-button rounded-full px-3 sm:px-4 py-1.5 text-[11px] sm:text-xs font-extrabold disabled:opacity-70 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1 min-w-[54px] sm:min-w-[58px]"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : t("buy")}
        </button>
      </td>
    </tr>
  );
};

const MonthsFooter = ({ months, t }) => (
  <div
    data-testid={`months-footer-${months}`}
    className="mt-3 rounded-xl bg-white/70 border border-orange-200 p-3"
  >
    <div className="text-[11px] uppercase tracking-[0.14em] font-extrabold text-orange-700 mb-1">
      {t("monthsFooterTitle")}
    </div>
    <p className="text-[12px] sm:text-[13px] leading-snug text-orange-950/90">
      {t("monthsFooterBody").replace("{months}", months)}
    </p>
  </div>
);


const Section = ({ title, subtitle, items, onBuy, loadingId, t }) => (
  <div className="mt-3 first:mt-2">
    {title && (
      <div className="px-1 mb-1">
        <div className="text-[10px] uppercase tracking-[0.16em] font-extrabold text-orange-700">
          {title}
        </div>
        {subtitle && (
          <div
            data-testid="section-subtitle"
            className="mt-1 inline-flex items-center gap-1.5 bg-green-50 border border-green-200 text-green-800 text-[11px] sm:text-xs font-bold px-2.5 py-1 rounded-full"
          >
            {subtitle}
          </div>
        )}
      </div>
    )}
    <div className="bg-white rounded-xl border border-orange-100 overflow-hidden">
      <table className="w-full border-collapse">
        <tbody>
          {items.map((p) => (
            <ProductRow
              key={p.id}
              product={p}
              onBuy={onBuy}
              loadingId={loadingId}
              t={t}
            />
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export default function OrderPage() {
  const { t } = useLang();
  const [mobile1, setMobile1] = useState("");
  const [mobile2, setMobile2] = useState("");
  const [email, setEmail] = useState("");
  const [loadingId, setLoadingId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const formRef = useRef(null);

  useEffect(() => {
    if (searchParams.get("cancelled") === "1") {
      toast.info(t("paymentCancelled"));
      searchParams.delete("cancelled");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, t]);

  const validate = () => {
    const m1 = normalize(mobile1);
    const m2 = normalize(mobile2);
    if (!m1 || m1.length < 8) {
      toast.error(t("invalidMobile"));
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return null;
    }
    if (m1 !== m2) {
      toast.error(t("mobileMismatch"));
      return null;
    }
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error(t("invalidEmail"));
      return null;
    }
    return { m1, email };
  };

  const handleBuy = async (product) => {
    const v = validate();
    if (!v) return;
    setLoadingId(product.id);
    try {
      const res = await axios.post(`${API}/checkout/session`, {
        package_id: product.id,
        mobile_number: v.m1,
        email: v.email,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Error";
      toast.error(msg);
      setLoadingId(null);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-black text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3">
          <a href="/" className="flex items-center gap-2.5 sm:gap-3" data-testid="logo-link">
            <img
              src={LOGO_URL}
              alt="Jan Mobile - Prepaid SIM"
              className="h-12 sm:h-14 w-auto rounded-full"
              data-testid="brand-logo"
            />
            <span
              data-testid="brand-title"
              className="font-extrabold tracking-tight text-lg sm:text-2xl leading-none"
            >
              <span className="text-white">JanMobiel</span>
              <span className="text-orange-500">TopUp</span>
            </span>
          </a>
          <LanguageSwitcher />
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 max-w-3xl w-full mx-auto px-3 sm:px-6 py-3 sm:py-4">
        {/* Compact details form */}
        <section
          ref={formRef}
          data-testid="details-form"
          className="bg-white rounded-xl border border-orange-100 p-3 sm:p-4"
        >
          <div className="flex items-baseline justify-between gap-3 mb-2">
            <h1 className="text-sm sm:text-base font-bold text-orange-950">
              {t("yourDetails")}
            </h1>
            <p className="hidden sm:block text-[11px] text-orange-900/60 text-right">
              {t("yourDetailsHint")}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-2">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-orange-900/70">
                {t("mobileLabel")}
              </label>
              <Input
                data-testid="mobile-input-1"
                value={mobile1}
                onChange={(e) => setMobile1(e.target.value)}
                placeholder={t("mobilePlaceholder")}
                inputMode="tel"
                autoComplete="tel"
                className="mt-0.5 h-9 border-orange-200 focus-visible:ring-orange-500 text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-orange-900/70">
                {t("confirmMobileLabel")}
              </label>
              <Input
                data-testid="mobile-input-2"
                value={mobile2}
                onChange={(e) => setMobile2(e.target.value)}
                placeholder={t("confirmMobilePlaceholder")}
                inputMode="tel"
                autoComplete="off"
                className="mt-0.5 h-9 border-orange-200 focus-visible:ring-orange-500 text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-orange-900/70">
                {t("emailLabel")}
              </label>
              <Input
                data-testid="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("emailPlaceholder")}
                autoComplete="email"
                className="mt-0.5 h-9 border-orange-200 focus-visible:ring-orange-500 text-sm"
              />
            </div>
          </div>
        </section>

        {/* Tabbed product lists */}
        <Tabs defaultValue="prepaid" className="mt-3">
          <TabsList
            data-testid="tabs-list"
            className="w-full grid grid-cols-3 gap-1 bg-orange-100/70 p-0.5 rounded-xl h-auto"
          >
            <TabsTrigger
              value="prepaid"
              data-testid="tab-prepaid"
              className="data-[state=active]:bg-white data-[state=active]:text-orange-700 data-[state=active]:shadow font-bold rounded-lg py-1.5 text-[11px] sm:text-xs"
            >
              {t("tabPrepaid")}
            </TabsTrigger>
            <TabsTrigger
              value="3month"
              data-testid="tab-3month"
              className="data-[state=active]:bg-white data-[state=active]:text-orange-700 data-[state=active]:shadow font-bold rounded-lg py-1.5 text-[11px] sm:text-xs"
            >
              {t("tab3Month")}
            </TabsTrigger>
            <TabsTrigger
              value="6month"
              data-testid="tab-6month"
              className="data-[state=active]:bg-white data-[state=active]:text-orange-700 data-[state=active]:shadow font-bold rounded-lg py-1.5 text-[11px] sm:text-xs"
            >
              {t("tab6Month")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="prepaid" data-testid="tab-content-prepaid">
            {ALL_PRODUCTS_GROUPED.map((g) => (
              <Section
                key={g.labelKey}
                title={t(g.labelKey)}
                subtitle={g.subtitleKey ? t(g.subtitleKey) : null}
                items={g.items}
                onBuy={handleBuy}
                loadingId={loadingId}
                t={t}
              />
            ))}
          </TabsContent>

          <TabsContent value="3month" data-testid="tab-content-3month">
            <div className="mt-1 rounded-2xl bg-amber-50/60 border border-amber-200 p-2.5 sm:p-3">
              <Section
                title={t("tab3Month")}
                subtitle={t("months3Promo")}
                items={MONTHS3_PRODUCTS}
                onBuy={handleBuy}
                loadingId={loadingId}
                t={t}
              />
              <MonthsFooter months={3} t={t} />
            </div>
          </TabsContent>

          <TabsContent value="6month" data-testid="tab-content-6month">
            <div className="mt-1 rounded-2xl bg-emerald-50/60 border border-emerald-200 p-2.5 sm:p-3">
              <Section
                title={t("tab6Month")}
                subtitle={t("months6Promo")}
                items={MONTHS6_PRODUCTS}
                onBuy={handleBuy}
                loadingId={loadingId}
                t={t}
              />
              <MonthsFooter months={6} t={t} />
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Footer / customer service */}
      <footer className="mt-4 bg-black text-white">
        <div className="max-w-3xl mx-auto px-3 sm:px-6 py-4">
          <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-orange-400 mb-2">
            {t("contactTitle")}
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            <a
              href={`https://wa.me/${WHATSAPP.replace(/\+/g, "")}`}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-whatsapp"
              className="flex items-center gap-2.5 rounded-lg bg-white/5 hover:bg-white/10 p-2.5 transition border border-white/10"
            >
              <div className="w-7 h-7 rounded-md bg-green-500/20 text-green-400 flex items-center justify-center">
                <MessageCircle size={14} />
              </div>
              <div className="leading-tight">
                <div className="text-[9px] uppercase tracking-wider text-white/50">
                  {t("whatsapp")}
                </div>
                <div className="text-xs sm:text-sm font-bold">{WHATSAPP}</div>
              </div>
            </a>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              data-testid="footer-email"
              className="flex items-center gap-2.5 rounded-lg bg-white/5 hover:bg-white/10 p-2.5 transition border border-white/10"
            >
              <div className="w-7 h-7 rounded-md bg-orange-500/20 text-orange-400 flex items-center justify-center">
                <Mail size={14} />
              </div>
              <div className="leading-tight">
                <div className="text-[9px] uppercase tracking-wider text-white/50">
                  {t("email")}
                </div>
                <div className="text-xs sm:text-sm font-bold">{SUPPORT_EMAIL}</div>
              </div>
            </a>
          </div>
          <div className="mt-3 text-[10px] text-white/40">
            &copy; {new Date().getFullYear()} Jan Mobile &middot; {t("footer")}
          </div>
        </div>
      </footer>
    </div>
  );
}
